import React from 'react';
import { productName, logoSrc } from '../brand';
import PageFooter from '../components/chrome/PageFooter';

// Public unauthenticated landing at "/" (FR-046..049). Design lifted
// from Sarah's "Option C" prototype with the visual structure intact
// — hero, worked example, stats strip — but all out-of-spec copy
// stripped: no "$48B / ±12% / 100+ / 1,400 / driver-level breakdown /
// monthly refresh / analyst review" claims (FR-019, FR-042, FR-049,
// FR-051). Worked example name is a placeholder per FR-048.

export default function Landing({ onSignUp, onSignIn, onMethodology }) {
  return (
    <div style={pageStyle}>
      <PublicTopbar onSignIn={onSignIn} onSignUp={onSignUp} onMethodology={onMethodology} />

      <section style={heroStyle}>
        <div style={heroInnerStyle}>
          <div style={heroLeftStyle}>
            <div style={eyebrowRuleStyle}>
              <span style={eyebrowRuleLineStyle} aria-hidden="true" />
              <span style={eyebrowStyle}>For data providers · 2026 edition</span>
            </div>
            <h1 style={heroTitleStyle}>
              What is your healthcare dataset actually{' '}
              <em style={accentItalicStyle}>worth?</em>
            </h1>
            <p style={heroLedeStyle}>
              {productName} is a transparent valuation model — not a black box. Calibrated on{' '}
              <strong>55+ documented data partnerships</strong>, the calculator returns a defensible
              tier and pricing range for your dataset, the per-patient value buyers pay for cohorts
              like yours, and the closest comparable deals from the calibration set.
            </p>
            <div style={heroCtaRowStyle}>
              <button type="button" onClick={onSignUp} style={primaryCtaStyle}>
                Begin a valuation <ArrowIcon />
              </button>
              <button type="button" onClick={onMethodology} style={secondaryCtaStyle}>
                Read methodology <ArrowIcon muted />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section style={workedSectionStyle}>
        <div style={workedInnerStyle}>
          <div style={workedLeftStyle}>
            <div style={eyebrowStyle}>A worked example</div>
            <h2 style={workedTitleStyle}>
              Acme Onc-EHR · 25K patients · 3 years.
            </h2>
            <p style={workedBodyStyle}>
              A premium oncology cohort with linked outcomes and multi-modal genomics. Below: the tier
              the model lands on, the annual pharma fee that tier carries, and the per-patient value
              implied — the same numbers you'll see at the end of the wizard.
            </p>
          </div>
          <div style={workedCardStyle}>
            <div style={workedCardLabelStyle}>Estimated annual pharma fee</div>
            <div style={workedCardAmountStyle}>
              <span style={workedCardCurrencyStyle}>$</span>1.0<span style={workedCardSuffixStyle}>M</span>
            </div>
            <div style={workedCardSubStyle}>Premium tier · estimate (not a quote)</div>
            <div style={workedSplitStyle}>
              <div>
                <div style={workedSubLabelStyle}>Tier</div>
                <div style={workedSubValueStyle}>Premium</div>
              </div>
              <div>
                <div style={workedSubLabelStyle}>Cohort size</div>
                <div style={workedSubValueStyle}>25,000</div>
              </div>
              <div>
                <div style={workedSubLabelStyle}>Follow-up</div>
                <div style={workedSubValueStyle}>3 years</div>
              </div>
              <div>
                <div style={workedSubLabelStyle}>Per patient / yr</div>
                <div style={workedSubValueStyle}>$40</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={statsSectionStyle}>
        <div style={statsInnerStyle}>
          <Stat figure="55+" label="documented data partnerships" />
          <Stat figure="6" label="pricing tiers, Entry → Ultra-Premium" />
          <Stat figure="3" label="revenue model views per dataset" />
          <Stat figure="5 min" label="time to first estimate" />
        </div>
      </section>

      <PageFooter onShowMethodology={onMethodology} />
    </div>
  );
}

function PublicTopbar({ onSignIn, onSignUp, onMethodology }) {
  return (
    <header style={topbarStyle}>
      <img src={logoSrc} alt={productName} style={topbarLogoStyle} />
      <nav style={topbarNavStyle} aria-label="Primary">
        <button type="button" style={topbarLinkStyle} onClick={onMethodology}>Methodology</button>
        <button type="button" style={topbarLinkStyle} onClick={onSignIn}>Sign in</button>
        <button type="button" style={topbarPrimaryStyle} onClick={onSignUp}>
          Begin a valuation
        </button>
      </nav>
    </header>
  );
}

function Stat({ figure, label }) {
  return (
    <div style={statStyle}>
      <div style={statFigureStyle}>{figure}</div>
      <div style={statLabelStyle}>{label}</div>
    </div>
  );
}

function ArrowIcon({ muted }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M5 12h14M13 5l7 7-7 7" stroke={muted ? 'currentColor' : 'currentColor'} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// styles
const pageStyle = { minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font)', color: 'var(--ink)', display: 'flex', flexDirection: 'column' };

const topbarStyle = {
  height: '76px',
  display: 'flex',
  alignItems: 'center',
  padding: '0 56px',
  borderBottom: '1px solid var(--line)',
  gap: '32px',
};
const topbarLogoStyle = { height: '48px', width: 'auto', display: 'block' };
const topbarNavStyle = { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '22px' };
const topbarLinkStyle = {
  background: 'transparent',
  border: 'none',
  color: 'var(--ink2)',
  fontSize: '13.5px',
  fontFamily: 'var(--font)',
  cursor: 'pointer',
  padding: 0,
};
const topbarPrimaryStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  height: '40px',
  padding: '0 18px',
  borderRadius: '999px',
  background: 'var(--ink)',
  color: 'var(--bg)',
  border: '1px solid var(--ink)',
  fontSize: '13.5px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};

const heroStyle = { padding: '88px 56px 64px', borderBottom: '1px solid var(--line)' };
const heroInnerStyle = { maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '32px' };
const heroLeftStyle = { maxWidth: '880px' };

const eyebrowRuleStyle = { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' };
const eyebrowRuleLineStyle = { display: 'inline-block', width: '36px', height: '1px', background: 'var(--accent)' };
const eyebrowStyle = { fontSize: '13px', color: 'var(--accent)', fontWeight: 500, letterSpacing: '0.02em' };

const heroTitleStyle = {
  fontFamily: 'var(--display)',
  fontWeight: 500,
  fontSize: 'clamp(48px, 8vw, 96px)',
  letterSpacing: '-0.03em',
  lineHeight: 1.0,
  margin: 0,
  color: 'var(--ink)',
};
const accentItalicStyle = { fontStyle: 'italic', color: 'var(--accent)' };

const heroLedeStyle = {
  marginTop: '32px',
  fontSize: '17px',
  lineHeight: 1.55,
  color: 'var(--ink2)',
  maxWidth: '640px',
};

const heroCtaRowStyle = { marginTop: '36px', display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' };

const primaryCtaStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  height: '54px',
  padding: '0 26px',
  borderRadius: '999px',
  background: 'var(--ink)',
  color: 'var(--bg)',
  border: '1px solid var(--ink)',
  fontSize: '15px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};
const secondaryCtaStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  height: '54px',
  padding: '0 10px',
  background: 'transparent',
  border: 'none',
  color: 'var(--ink2)',
  fontSize: '15px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};

const workedSectionStyle = { padding: '72px 56px', borderBottom: '1px solid var(--line)' };
const workedInnerStyle = { maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '48px', alignItems: 'start' };
const workedLeftStyle = {};
const workedTitleStyle = {
  fontFamily: 'var(--display)',
  fontWeight: 500,
  fontSize: '36px',
  letterSpacing: '-0.02em',
  lineHeight: 1.1,
  margin: '8px 0 16px',
  color: 'var(--ink)',
};
const workedBodyStyle = { fontSize: '15px', lineHeight: 1.6, color: 'var(--ink2)', maxWidth: '500px' };

const workedCardStyle = {
  padding: '32px',
  background: 'var(--paper)',
  border: '1px solid var(--line)',
  borderRadius: '10px',
  boxShadow: 'var(--shadow-sm)',
};
const workedCardLabelStyle = { fontSize: '12.5px', color: 'var(--muted)' };
const workedCardAmountStyle = {
  fontFamily: 'var(--display)',
  fontWeight: 500,
  fontSize: '72px',
  letterSpacing: '-0.04em',
  lineHeight: 1,
  color: 'var(--accent)',
  marginTop: '12px',
};
const workedCardCurrencyStyle = { fontSize: '40px', color: 'var(--muted)', marginRight: '4px' };
const workedCardSuffixStyle = { fontSize: '36px', color: 'var(--muted)' };
const workedCardSubStyle = { fontSize: '12.5px', color: 'var(--muted)', marginTop: '10px' };
const workedSplitStyle = {
  marginTop: '20px',
  paddingTop: '20px',
  borderTop: '1px solid var(--line)',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
};
const workedSubLabelStyle = { fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const workedSubValueStyle = { fontSize: '17px', fontWeight: 600, color: 'var(--ink)', marginTop: '4px' };

const statsSectionStyle = { padding: '64px 56px', background: 'var(--paper)' };
const statsInnerStyle = { maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '32px' };
const statStyle = {};
const statFigureStyle = {
  fontFamily: 'var(--display)',
  fontWeight: 500,
  fontSize: '52px',
  letterSpacing: '-0.025em',
  lineHeight: 1,
  color: 'var(--ink)',
};
const statLabelStyle = { fontSize: '13.5px', color: 'var(--ink2)', marginTop: '10px', lineHeight: 1.4, maxWidth: '220px' };
